"use client"

import type { OImageProps } from "#modules/optimization/media/utils"
import type { OmitFromDU } from "#types/utils"

import React, { useRef } from "react"
import { getImageProps } from "next/image"
import { twJoin } from "tailwind-merge"

import { useLazyLoad } from "#modules/optimization/media/lazyload"
import { EMPTY_IMAGE_SRC, getPropsForNextImage } from "#modules/optimization/media/utils"

/**
 * Lazy version of the next/image component, the base for lazy loading an optimized image - only loads image when scrolled into viewport with slight offset from @useLazyLoad()
 * Lazy loaded images get faded in
 * TODO find out why blur placeholder is not updated/overlayed while image is loading - in Next/Image it works (in slow network mode, you can see that the loaded image data is streamed into the placeholder, here it is not working)
 */
export function LazyOImage(props: OmitFromDU<OImageProps, "lazy">) {
  const imageRef = useRef<HTMLImageElement>(null)
  const hasBlurPlaceholder = props.placeholder === "blur"

  const { nextImageProps, nonMediaImageProps } = getPropsForNextImage(props)

  const {
    props: { src, srcSet, className, ...imageProps },
  } = getImageProps(nextImageProps)

  useLazyLoad(imageRef, src)

  return (
    // eslint-disable-next-line @next/next/no-img-element,jsx-a11y/alt-text
    <img
      draggable={false}
      {...imageProps}
      style={{ ...imageProps.style, aspectRatio: `${nonMediaImageProps.width} / ${nonMediaImageProps.height}` }}
      className={twJoin(className, "lazy", !hasBlurPlaceholder && "lazy-hidden")}
      src={hasBlurPlaceholder ? EMPTY_IMAGE_SRC : undefined}
      onLoad={e => {
        // Remove blur placeholder properties after finished loading - check if data-src attribute was removed already, if not it means that only the empty placeholder in src got loaded
        if (hasBlurPlaceholder && !(e.target as HTMLImageElement).dataset.src) {
          imageRef.current?.style.removeProperty("background-image")
          imageRef.current?.style.removeProperty("background-size")
          imageRef.current?.style.removeProperty("background-position")
          imageRef.current?.style.removeProperty("background-repeat")
        }
      }}
      data-srcset={srcSet}
      data-src={src}
      // Used to identify which lazy images to hide completely and which to show (inside globals.css)
      data-placeholder={hasBlurPlaceholder ? "blur" : undefined}
      ref={imageRef}
      width={nonMediaImageProps.width}
      height={nonMediaImageProps.height}
    />
  )
}
